import {useState} from 'react'
import {X, Search, Users, Crown} from 'lucide-react'
import type {Team} from '../../types'
import {useApp} from '../../context/AppContext'
import Avatar from '../common/Avatar'
import Modal from '../common/Modal'

interface TeamFormProps {
  team?: Team
  onClose: () => void
}

const colors = ['#3570E6', '#7c3aed', '#0891b2', '#059669', '#d97706', '#e11d48']

export default function TeamForm({team, onClose}: TeamFormProps) {
  const {state, apiCreateTeam, apiUpdateTeam} = useApp()
  const [name, setName] = useState(team?.name || '')
  const [description, setDescription] = useState(team?.description || '')
  const [color, setColor] = useState(team?.color || colors[0])
  const [leadId, setLeadId] = useState(team?.leadId || '')
  const [memberIds, setMemberIds] = useState<string[]>(
    Array.from(new Set([...(team?.memberIds || []), ...(team?.leadId ? [team.leadId] : [])]))
  )
  const [search, setSearch] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const query = search.trim().toLocaleLowerCase('pt-BR')
  const availableMembers = state.users.filter(
    (user) =>
      !memberIds.includes(user.id) &&
      (user.name + ' ' + user.email).toLocaleLowerCase('pt-BR').includes(query)
  )
  const close = () => {
    if (!submitting) onClose()
  }

  const toggleMember = (id: string) => {
    setMemberIds((current) =>
      current.includes(id) ? current.filter((memberId) => memberId !== id) : [...current, id]
    )
    if (leadId === id) setLeadId('')
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!name.trim() || submitting) return
    setSubmitting(true)
    setError('')
    try {
      const values = {name: name.trim(), description: description.trim(), color, leadId, memberIds}
      if (team) await apiUpdateTeam({...team, ...values})
      else await apiCreateTeam(values)
      onClose()
    } catch {
      setError('Não foi possível salvar a equipe. Seus dados foram preservados; tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen
      closeDisabled={submitting}
      onClose={close}
      title={team ? 'Editar equipe' : 'Nova equipe'}
      size='lg'
    >
      <form onSubmit={handleSubmit} className='p-5 md:p-6 space-y-5'>
        <div className='flex items-center gap-3 p-4 rounded-xl bg-slate-50'>
          <div className='p-3 rounded-xl bg-white' style={{color}}>
            <Users size={22} />
          </div>
          <div>
            <h3 className='text-sm font-semibold text-slate-800'>
              {name.trim() || 'Sua nova equipe'}
            </h3>
            <p className='text-xs text-slate-500 mt-1'>
              {memberIds.length} pessoas selecionadas ·{' '}
              {leadId ? 'Liderança definida' : 'Liderança opcional'}
            </p>
          </div>
        </div>
        {error && (
          <p role='alert' className='p-3 rounded-xl bg-red-50 text-sm text-red-600'>
            {error}
          </p>
        )}
        <fieldset disabled={submitting} className='space-y-5'>
          <div>
            <label htmlFor='team-name' className='block text-sm font-medium text-slate-700 mb-1'>
              Nome da equipe *
            </label>
            <input
              id='team-name'
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              required
              autoFocus
              placeholder='Ex.: Engenharia de dados'
              className='w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm'
            />
          </div>
          <div>
            <label
              htmlFor='team-description'
              className='block text-sm font-medium text-slate-700 mb-1'
            >
              Objetivo e responsabilidades
            </label>
            <textarea
              id='team-description'
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
              placeholder='Descreva o foco e as responsabilidades desta equipe.'
              className='w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm resize-y'
            />
          </div>
          <div>
            <label htmlFor='team-color' className='block text-sm font-medium text-slate-700 mb-2'>
              Cor da equipe
            </label>
            <div className='flex flex-wrap items-center gap-2'>
              {colors.map((value) => (
                <button
                  key={value}
                  type='button'
                  aria-label={'Usar cor ' + value}
                  aria-pressed={color === value}
                  onClick={() => setColor(value)}
                  className={
                    'w-8 h-8 rounded-full border-2 ' +
                    (color === value
                      ? 'ring-2 ring-offset-2 ring-brand-300 border-white'
                      : 'border-transparent')
                  }
                  style={{backgroundColor: value}}
                />
              ))}
              <input
                id='team-color'
                aria-label='Escolher outra cor da equipe'
                type='color'
                value={color}
                onChange={(event) => setColor(event.target.value)}
                className='w-9 h-9 rounded-lg border border-slate-200 cursor-pointer'
              />
            </div>
          </div>
          <div>
            <label htmlFor='team-lead' className='block text-sm font-medium text-slate-700 mb-1'>
              Liderança
            </label>
            <select
              id='team-lead'
              value={leadId}
              onChange={(event) => {
                const id = event.target.value
                setLeadId(id)
                if (id)
                  setMemberIds((current) => (current.includes(id) ? current : [...current, id]))
              }}
              className='w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm'
            >
              <option value=''>Definir depois</option>
              {state.users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
            <p className='text-xs text-slate-400 mt-1.5'>
              A pessoa responsável também fará parte dos membros da equipe.
            </p>
          </div>
          <div>
            <h3 className='text-sm font-semibold text-slate-700 mb-2'>
              Membros selecionados ({memberIds.length})
            </h3>
            <div className='space-y-2 max-h-48 overflow-y-auto'>
              {memberIds.map((id) => {
                const member = state.users.find((user) => user.id === id)
                if (!member) return null
                return (
                  <div
                    key={id}
                    className='flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-xl'
                  >
                    <Avatar name={member.name} color={member.avatarColor} size='sm' />
                    <span className='text-sm text-slate-700 flex-1'>{member.name}</span>
                    {leadId === id && (
                      <Crown size={15} className='text-amber-500' aria-label='Liderança' />
                    )}
                    <button
                      type='button'
                      aria-label={'Remover ' + member.name + ' da equipe'}
                      onClick={() => toggleMember(id)}
                      className='devops-icon-button'
                    >
                      <X size={14} />
                    </button>
                  </div>
                )
              })}
              {memberIds.length === 0 && (
                <p className='text-sm text-slate-500 p-3 rounded-xl border border-dashed border-slate-200'>
                  Selecione as pessoas que participarão desta equipe.
                </p>
              )}
            </div>
            <div className='flex items-center gap-2 mt-3'>
              <Search size={16} className='text-slate-400' />
              <input
                aria-label='Buscar pessoas para adicionar à equipe'
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder='Buscar por nome ou e-mail...'
                className='w-full px-3 py-2 rounded-xl border border-slate-200 text-sm'
              />
            </div>
            <div className='mt-2 max-h-40 overflow-y-auto space-y-1'>
              {availableMembers.map((user) => (
                <button
                  type='button'
                  key={user.id}
                  onClick={() => toggleMember(user.id)}
                  className='w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg hover:bg-slate-50'
                >
                  <Avatar name={user.name} color={user.avatarColor} size='sm' />
                  <span className='flex-1 min-w-0'>
                    <span className='block text-sm text-slate-700 truncate'>{user.name}</span>
                    <span className='block text-xs text-slate-400 truncate'>{user.email}</span>
                  </span>
                  <span className='text-xs text-brand-600'>Adicionar</span>
                </button>
              ))}
              {availableMembers.length === 0 && (
                <p className='text-xs text-slate-500 py-3'>
                  {query
                    ? 'Nenhuma pessoa corresponde à busca.'
                    : 'Todas as pessoas disponíveis já foram selecionadas.'}
                </p>
              )}
            </div>
          </div>
        </fieldset>
        <div className='flex justify-end gap-3 pt-4 border-t border-slate-100'>
          <button
            type='button'
            disabled={submitting}
            onClick={close}
            className='devops-secondary-button'
          >
            Cancelar
          </button>
          <button
            type='submit'
            disabled={!name.trim() || submitting}
            className='devops-primary-button disabled:opacity-50'
          >
            {submitting ? 'Salvando...' : team ? 'Salvar alterações' : 'Criar equipe'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
